import React, { useState } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    TextField,
    Alert,
    CircularProgress,
    Chip
} from '@mui/material';
import { Send as SendIcon, SmartToy as SmartToyIcon } from '@mui/icons-material';
import RichTextContent from '../../common/RichTextContent';
import { useTranslation } from '../../../i18n';

interface FreeTextPlayerProps {
    lesson: any;
    onComplete: () => void;
}

const FreeTextPlayer: React.FC<FreeTextPlayerProps> = ({ lesson, onComplete }) => {
    const { t } = useTranslation();
    const content = lesson.content || {};
    const questions = content.questions || [];

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answer, setAnswer] = useState('');
    const [feedback, setFeedback] = useState<any>(null);
    const [submitting, setSubmitting] = useState(false);
    const [allCompleted, setAllCompleted] = useState(false);
    // Issue #197 (EU-KI-Act): true sobald eine Antwort auf Trainer-Freigabe wartet —
    // dann darf die Lektion nicht client-seitig abgeschlossen werden.
    const [reviewPending, setReviewPending] = useState(false);

    const currentQuestion = questions[currentQuestionIndex];

    const handleSubmit = async () => {
        setSubmitting(true);

        try {
            // Call AI grading endpoint
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/ai/grade-answer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    lessonId: lesson.id,
                    answer,
                    questionIndex: currentQuestionIndex
                })
            });

            const data = await response.json();

            if (data.success) {
                setFeedback(data);
                if (data.requiresTeacherReview) {
                    setReviewPending(true);
                }
            } else {
                setFeedback({ error: 'Fehler bei der Bewertung' });
            }
        } catch (error) {
            console.error('Error submitting answer:', error);
            setFeedback({ error: 'Fehler bei der Bewertung' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleNext = () => {
        setAnswer('');
        setFeedback(null);

        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            setAllCompleted(true);
        }
    };

    if (questions.length === 0) {
        return <Alert severity="warning">Keine Fragen konfiguriert.</Alert>;
    }

    if (allCompleted) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom>
                    Alle Fragen beantwortet!
                </Typography>
                {reviewPending ? (
                    // Issue #197: Pflicht-Review — Abschluss passiert server-seitig
                    // erst nach der Trainer-Freigabe, kein onComplete hier.
                    <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }} data-testid="review-pending-note">
                        {t('aiGrading.reviewPendingLesson', 'Deine Antworten wurden eingereicht. Die KI-Vorbewertung wird von deiner Trainer:in geprüft — die Lektion wird nach der Freigabe automatisch abgeschlossen.')}
                    </Alert>
                ) : (
                    <Button variant="contained" onClick={onComplete} sx={{ mt: 2 }}>
                        Lektion abschließen
                    </Button>
                )}
            </Paper>
        );
    }

    const wordCount = answer.trim().split(/\s+/).filter(w => w).length;

    return (
        <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                        Frage {currentQuestionIndex + 1} von {questions.length}
                    </Typography>
                    {currentQuestion.maxScore && (
                        <Chip label={`Max. ${currentQuestion.maxScore} Punkte`} color="primary" />
                    )}
                </Box>

                <RichTextContent
                    html={currentQuestion.question}
                    sx={{ mb: 3 }}
                />

                {/* Issue #197 (EU-KI-Act Art. 50): Transparenz VOR der Interaktion */}
                <Alert severity="info" icon={<SmartToyIcon fontSize="small" />} sx={{ mb: 2 }} data-testid="ai-grading-disclaimer">
                    {t('aiGrading.disclaimer', 'Deine Antwort wird durch ein KI-System vorbewertet. Bitte gib keine sensiblen persönlichen Informationen ein. Du hast jederzeit das Recht auf eine Überprüfung durch deine Trainer:in.')}
                </Alert>

                <TextField
                    fullWidth
                    multiline
                    rows={8}
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Geben Sie Ihre Antwort hier ein..."
                    disabled={!!feedback}
                    helperText={`${wordCount} Wörter${
                        currentQuestion.minWords
                            ? ` (Minimum: ${currentQuestion.minWords})`
                            : ''
                    }`}
                />

                {feedback && !feedback.error && (
                    <Box sx={{ mt: 3 }}>
                        <Alert
                            severity={feedback.passed ? 'success' : 'warning'}
                            sx={{ mb: 2 }}
                        >
                            <Typography variant="h6">
                                {feedback.score} / {feedback.maxScore} Punkte ({feedback.percentage}%)
                            </Typography>
                        </Alert>

                        {/* Issue #197: Kennzeichnung der KI-Bewertung inkl. Modell + Zeitpunkt */}
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }} data-testid="ai-grading-attribution">
                            {t('aiGrading.attribution', 'KI-assistierte Bewertung — bei Beanstandung wende dich an deine Trainer:in.')}
                            {feedback.model ? ` (${t('aiGrading.model', 'Modell')}: ${feedback.model}${feedback.gradedAt ? `, ${new Date(feedback.gradedAt).toLocaleString()}` : ''})` : ''}
                        </Typography>

                        {feedback.requiresTeacherReview && (
                            <Alert severity="info" sx={{ mb: 2 }} data-testid="review-pending-alert">
                                {t('aiGrading.reviewPending', 'Diese Bewertung wird von deiner Trainer:in geprüft und zählt erst nach der Freigabe.')}
                            </Alert>
                        )}

                        <Typography variant="subtitle2" gutterBottom>
                            Feedback:
                        </Typography>
                        <Typography variant="body2" paragraph>
                            {feedback.feedback}
                        </Typography>

                        {feedback.strengths && feedback.strengths.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="success.main" gutterBottom>
                                    Stärken:
                                </Typography>
                                <ul>
                                    {feedback.strengths.map((strength: string, i: number) => (
                                        <li key={i}>
                                            <Typography variant="body2">{strength}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        )}

                        {feedback.improvements && feedback.improvements.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }} gutterBottom>
                                    Verbesserungsvorschläge:
                                </Typography>
                                <ul>
                                    {feedback.improvements.map((improvement: string, i: number) => (
                                        <li key={i}>
                                            <Typography variant="body2">{improvement}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Musterlösung wird bewusst nicht angezeigt */}

                {feedback?.error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {feedback.error}
                    </Alert>
                )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                {!feedback ? (
                    <Button
                        variant="contained"
                        startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                        onClick={handleSubmit}
                        disabled={!answer.trim() || submitting}
                    >
                        {submitting ? 'Wird bewertet...' : 'Antwort einreichen'}
                    </Button>
                ) : (
                    <Button variant="contained" onClick={handleNext}>
                        {currentQuestionIndex < questions.length - 1
                            ? 'Nächste Frage'
                            : 'Abschließen'}
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default FreeTextPlayer;
